import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { useUserDashboard } from '../../hooks/useUserDashboard';
import { useInvoiceSearch } from '../../hooks/useInvoiceSearch';
import { setCurrentPage } from '../../features/invoice-search/invoice-search.slice';
import { fetchInvoiceSearchApi } from '../../api/invoice-search';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import { useDispatch } from 'react-redux';
import RequestCreditInvoiceModal from './RequestCreditInvoiceModal';
import SendInvoiceToCustomerModal from './send-invoice-to-customer-modal.component';
import EmailTrackingModal from './email-tracking-modal.component';
import {
    TableRow,
    TableCell,
    TableHeader,
    TableColumn,
    MobileCardRow,
    MobileCardWrapper,
    TableBody,
} from './table-components';
import './../../scss/components/invoice-table-detailed.style.scss';
import { getItem } from '../../helper/localstorage.helper';
import { SearchIcon, ArrowLeftIcon, ArrowRightIcon, DownloadIcon, EditIcon, RequestCreditIcon, TrackingIcon, SendIcon } from './../../assets/dashboard-icon.svg';
import { formatAmount, formatDate, getStatusBadgeClass, mapInvoiceStatus, getExpressPaymentStatus, getStageBadgeClass, handleDownloadInvoice, sentInvoicesStatus } from '../../helper/common.helper';

import { InvoiceTableSkeleton } from './manage-invoice-skeleton.component';

/* TruncatedText — shows a dark-pill native-style tooltip on hover ONLY
   when the inner text actually overflows its container (i.e. is showing
   the ellipsis). Uses `scrollWidth > clientWidth` as the overflow check
   and re-evaluates via ResizeObserver so column resizes / sidebar
   collapse / zoom / language switches all keep the tooltip decision
   accurate. When the text fits in full there is NO tooltip at all — so
   the hover feels natural and only surfaces info the user actually
   needs.

   Duplicated verbatim from my-client-section.component.tsx so both
   consumers stay independent. The `.mi-tt` wrapper class + tooltip
   pseudo-element styling already live globally in
   scss/components/manage-invoice.style.scss (see the `mi-tt` block
   there). */
const TruncatedText: React.FC<{
    text: string;
    className?: string;
}> = ({ text, className }) => {
    const ref = useRef<HTMLSpanElement>(null);
    const [overflows, setOverflows] = useState(false);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const check = () => {
            // +1 tolerance for sub-pixel rendering on fractional widths
            setOverflows(el.scrollWidth > el.clientWidth + 1);
        };
        check();
        const ro = new ResizeObserver(check);
        ro.observe(el);
        return () => ro.disconnect();
    }, [text]);

    /* Wrapped in `.mi-tt` because the inner span has `overflow: hidden`
       (needed for the ellipsis) — and `overflow: hidden` would clip a
       tooltip pseudo-element on that same span. The wrapper has no
       overflow constraint, so its `::after` tooltip can render freely
       above the row. The wrapper carries the `data-tip` attribute that
       the SCSS picks up. */
    return (
        <span className="mi-tt" data-tip={overflows ? text : undefined}>
            <span ref={ref} className={className}>
                {text}
            </span>
        </span>
    );
};

/* MiddleTruncatedText — like TruncatedText, but when the value does not fit its
   column it truncates in the MIDDLE (e.g. "80…1547" = first `head` chars + … +
   last `tail` chars) instead of a trailing ellipsis, so the meaningful start and
   end of an invoice number stay visible. A hidden full-text measurer decides the
   fit, so swapping to the shorter middle form never causes a resize loop. The
   full value is shown on hover, and when it fits it renders in full. */
const MiddleTruncatedText: React.FC<{
    text: string;
    className?: string;
    head?: number;
    tail?: number;
}> = ({ text, className, head = 2, tail = 4 }) => {
    const wrapRef = useRef<HTMLSpanElement>(null);
    const measureRef = useRef<HTMLSpanElement>(null);
    const [truncate, setTruncate] = useState(false);

    useLayoutEffect(() => {
        const wrap = wrapRef.current, measure = measureRef.current;
        if (!wrap || !measure) return;
        const check = () => {
            // Measure the ALWAYS-full-text hidden span vs the available width;
            // measuring the full text (not the visible, possibly-shortened one)
            // avoids a fit→shorten→fits-again feedback loop.
            setTruncate(measure.scrollWidth > wrap.clientWidth + 1);
        };
        check();
        const ro = new ResizeObserver(check);
        ro.observe(wrap);
        return () => ro.disconnect();
    }, [text]);

    const display = truncate && text.length > head + tail + 1
        ? `${text.slice(0, head)}…${text.slice(-tail)}`
        : text;

    return (
        // Outer `.mi-tt` carries the hover tooltip and must NOT clip (else the
        // tooltip pseudo-element gets cut off). The inner span does the clipping
        // + width measuring.
        <span className="mi-tt" data-tip={truncate ? text : undefined} style={{ display: 'block', width: '100%', maxWidth: '100%' }}>
            <span
                ref={wrapRef}
                style={{ display: 'block', position: 'relative', overflow: 'hidden', width: '100%', maxWidth: '100%' }}
            >
                {/* Hidden measurer carries the SAME className as the visible text so
                    it measures at the identical font — otherwise it over-/under-
                    estimates the fit and truncates (or not) at the wrong width. */}
                <span
                    ref={measureRef}
                    aria-hidden="true"
                    className={className}
                    style={{ position: 'absolute', left: 0, top: 0, visibility: 'hidden', whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip', maxWidth: 'none', pointerEvents: 'none' }}
                >
                    {text}
                </span>
                <span className={className} style={{ whiteSpace: 'nowrap' }}>{display}</span>
            </span>
        </span>
    );
};

interface InvoiceTableDetailedProps {
    className?: string;
}

const CalendarIcon: React.FC = () => (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="4.5" width="18" height="17" rx="2.5" stroke="currentColor" strokeWidth="2" />
        <path d="M3 9h18M8 2.5v4M16 2.5v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const LightningIcon: React.FC = () => (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" />
    </svg>
);

const SortArrowIcon: React.FC = () => (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 5v14M6 13l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const InvoiceTableDetailed: React.FC<InvoiceTableDetailedProps> = ({
    className = ''
}) => {
    const {
        filters,
        setType,
        setStatus,
        clearDateTime
    } = useUserDashboard();

    const {
        searchResults,
        loading: searchLoading,
        error: searchError,
        pagination: searchPagination,
        filters: searchFilters,
        performSearch,
        updateSearchQuery,
        clear: clearSearch,
    } = useInvoiceSearch();

    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const language = getItem('i18nextLng') || 'en-US';

    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [selectedStatus, setSelectedStatus] = useState<string>('');
    const [selectedStage, setSelectedStage] = useState<string>('');
    const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState<boolean>(false);
    const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<number | null>(null);
    const [editingInvoiceNumber, setEditingInvoiceNumber] = useState<string | null>(null);
    const [showCreditInvoiceModal, setShowCreditInvoiceModal] = useState<boolean>(false);
    const [selectedInvoiceForCredit, setSelectedInvoiceForCredit] = useState<any>(null);
    const [showSendInvoiceModal, setShowSendInvoiceModal] = useState<boolean>(false);
    const [selectedInvoiceForSend, setSelectedInvoiceForSend] = useState<any>(null);
    const [trackingInvoice, setTrackingInvoice] = useState<{
        invoiceId: number;
        invoiceStatus: number;
        isSalaryCreated: boolean;
        invoiceSalaryStatus: number;
    } | null>(null);
    const [showVersionRestrictionModal, setShowVersionRestrictionModal] = useState<boolean>(false);
    const [selectedInvoiceVersion, setSelectedInvoiceVersion] = useState<number | null>(null);
    const user = JSON.parse(getItem('user') as string);
    const isLoginWithBankId = user?.isLoginWithBankId;
    const invoiceSearchVal = new URLSearchParams(window.location.search).get('invoiceSearch') || '';
    const currentVersion = localStorage.getItem('currentappversion') || 'v1';
    const isV4Mode = currentVersion === 'v2';

    const dropdownRef = useRef<HTMLDivElement>(null);
    const [initialLoad, setInitialLoad] = useState<boolean>(true);
    const [isPaging, setIsPaging] = useState<boolean>(false);

    /* Deep-link highlight — when the user clicks a row on the dashboard we land
       here with ?highlightInvoice=<number>. We remember it once (the URL is
       cleaned right after so a refresh won't re-trigger the animation) and use
       it to locate + scroll to + pulse the matching row. */
    const [highlightedInvoiceNumber, setHighlightedInvoiceNumber] = useState<string | null>(
        () => new URLSearchParams(window.location.search).get('highlightInvoice')
    );
    const highlightHandledRef = useRef<boolean>(false);

    const getClientInitial = (name?: string) => {
        const parts = (name || '').trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) return '?';
        const letters = parts.slice(0, 2).map((p) => p.charAt(0)).join('');
        return letters.toUpperCase();
    };

    const getAvatarVariant = (name?: string) => {
        const key = (name || '').trim();
        let sum = 0;
        for (let i = 0; i < key.length; i++) sum += key.charCodeAt(i);
        return (sum % 5) + 1;
    };

    const formatDisplayDate = (dateString?: string) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return '';
        /* yyyy-mm-dd — universal ISO-8601 format, zero-padded month/day,
           identical in every locale so en-US and sv-SE users see the
           same string. Replaces the previous locale-aware
           "12 Aug 2026" / "12 aug. 2026" output. */
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    const getRelativeDue = (dateString?: string) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return '';
        const today = new Date();
        const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const startOfDue = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const diffDays = Math.round((startOfDue.getTime() - startOfToday.getTime()) / 86400000);
        if (diffDays === 0) return t('DUE_TODAY', { defaultValue: 'Today' });
        if (diffDays > 0) {
            return diffDays === 1
                ? t('DUE_IN_DAY', { count: diffDays, defaultValue: 'in {{count}} day' })
                : t('DUE_IN_DAYS', { count: diffDays, defaultValue: 'in {{count}} days' });
        }
        const ago = Math.abs(diffDays);
        return ago === 1
            ? t('DUE_DAY_AGO', { count: ago, defaultValue: '{{count}} day ago' })
            : t('DUE_DAYS_AGO', { count: ago, defaultValue: '{{count}} days ago' });
    };

    const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setHighlightedInvoiceNumber(null);
        updateSearchQuery(query);

        if (!query.trim()) {
            clearSearch();
            setIsSearchDropdownOpen(false);
            performSearch(buildParams(''));
            return;
        }

        try {
            setIsSearchDropdownOpen(true);
            await performSearch(buildParams(query));
        } catch (error: any) {
            console.error('Search error:', error);
            setIsSearchDropdownOpen(false);
        }
    };

    const handleRequestCreditInvoice = (invoice: any) => {
        setSelectedInvoiceForCredit(invoice);
        setShowCreditInvoiceModal(true);
    };

    const handleCloseCreditInvoiceModal = () => {
        setShowCreditInvoiceModal(false);
        setSelectedInvoiceForCredit(null);
    };

    const handleCreditInvoiceSuccess = () => {
        performSearch(buildParams(searchFilters.searchQuery, searchPagination.currentPage));
    };

    const handleSendInvoice = (invoice: any) => {
        setSelectedInvoiceForSend(invoice);
        setShowSendInvoiceModal(true);
    };

    const handleCloseSendInvoiceModal = () => {
        setShowSendInvoiceModal(false);
        setSelectedInvoiceForSend(null);
    };

    const handleSendInvoiceSuccess = () => {
        performSearch(buildParams(searchFilters.searchQuery, searchPagination.currentPage));
    };

    /**
     * Translation key explaining why the freelancer cannot resend this invoice,
     * or null when they can. Single source for both the button's `disabled` state
     * and its tooltip, so the two can never disagree.
     *
     * The first invoice email is always sent by admin; the freelancer may only
     * resend afterwards, typically after correcting the customer's address. This
     * is a cheap client-side gate - the send modal re-checks the API's
     * authoritative canSend/blockReason before letting a send through.
     * 2 = InvoiceApprovalStatus.Approved on the API.
     */
    const getSendInvoiceBlockedKey = (invoice: any): string | null => {
        if (invoice?.invoiceApprovalStatus !== 2) return 'INVOICE_NOT_APPROVED_BY_ADMIN';
        if (!invoice?.isEmailSent) return 'INVOICE_NOT_SENT_BY_ADMIN_YET';

        // Admin may still change the invoice while a request is open, so the row's
        // other actions lock too - keep this button consistent with them.
        const hasPendingRequest = invoice?.isEditInvoiceRequested
            && !invoice?.isEditInvoiceRequestApproved
            && !invoice?.isEditInvoiceRequestRejected;
        if (hasPendingRequest || invoice?.isCreditInvoiceRequested) return 'CANNOT_RESEND_WHILE_REQUEST_PENDING';

        if (invoice?.isHaveCreditInvoice || invoice?.isBooked || invoice?.isReminderInvoice) {
            return 'CANNOT_RESEND_THIS_INVOICE';
        }
        return null;
    };

    const handleOpenEmailTracking = (invoice: any) => {
        setTrackingInvoice({
            invoiceId: invoice.id,
            invoiceStatus: invoice.invoiceStatus,
            isSalaryCreated: !!invoice.isSalaryCreated,
            invoiceSalaryStatus: invoice.invoiceSalaryStatus,
        });
    };

    const handleCloseEmailTracking = () => {
        setTrackingInvoice(null);
    };

    const handleEditInvoiceButtonClick = async (invoice: any) => {
        // Check if invoice was created with V1 and current version is V4
        // const invoiceToolVersion = invoice.invoiceToolVersion || 1; // Default to 1 if not specified
        
        // if (invoiceToolVersion === 1 && isV4Mode) {
           
        //     setSelectedInvoiceVersion(invoiceToolVersion);
        //     setShowVersionRestrictionModal(true);
        //     return;
        // }

        setEditingInvoiceNumber(invoice.invoiceNumber);

        const createdDate = invoice.createdDate || new Date().toISOString();

        const params = new URLSearchParams({
            lang: language,
            invoiceNumber: invoice.invoiceNumber,
            edit: 'true',
            createdDate: createdDate
        });

        navigate(`/v2/create-invoice?${params.toString()}`);
    };

    // Build unified params for every fetch — search query + filters always go together
    const buildParams = (query: string, page = 1) => ({
        PageNumber: page,
        PageSize: 10,
        ...(query?.trim() && { searchQuery: query.trim() }),
        ...(filters.status && { status: filters.status }),
        ...(filters.type && { type: filters.type }),
    });

    // Optimistically move the active page indicator before the fetch resolves,
    // so clicking a page feels instant instead of waiting on the network.
    const goToPage = (page: number) => {
        setHighlightedInvoiceNumber(null);
        setIsPaging(true);
        dispatch(setCurrentPage(page));
        Promise.resolve(performSearch(buildParams(searchFilters.searchQuery, page))).finally(() => setIsPaging(false));
    };

    const isInitialMount = useRef(true);

    useEffect(() => {
        clearDateTime();
        const initialQuery = invoiceSearchVal || '';

        // Strip ?highlightInvoice from the URL (keep the value in state) so a
        // manual refresh / back navigation doesn't replay the animation.
        const targetHighlight = highlightedInvoiceNumber;
        if (targetHighlight) {
            const url = new URL(window.location.href);
            url.searchParams.delete('highlightInvoice');
            window.history.replaceState({}, '', url.toString());
        }

        // Deep-link highlight (only when there's no explicit search query):
        // locate which page the invoice lives on in the full list, open that
        // page, then let the scroll/pulse effect below run once it renders.
        if (targetHighlight && !initialQuery) {
            (async () => {
                try {
                    const size = 10;
                    // Cheap probe for the total, then one bulk fetch to find the
                    // invoice's position in the full (unfiltered) ordering.
                    const probe = await fetchInvoiceSearchApi({
                        PageNumber: 1,
                        PageSize: 1,
                        ...(filters.status && { status: filters.status }),
                        ...(filters.type && { type: filters.type }),
                    });
                    const total = probe?.totalCount || 0;
                    let targetPage = 1;
                    if (total > 0) {
                        const full = await fetchInvoiceSearchApi({
                            PageNumber: 1,
                            PageSize: total,
                            ...(filters.status && { status: filters.status }),
                            ...(filters.type && { type: filters.type }),
                        });
                        const idx = (full?.response || []).findIndex(
                            (inv: any) => String(inv.invoiceNumber) === String(targetHighlight)
                        );
                        if (idx >= 0) targetPage = Math.floor(idx / size) + 1;
                    }
                    dispatch(setCurrentPage(targetPage));
                    await performSearch(buildParams('', targetPage));
                } catch (e) {
                    console.error('Highlight locate failed:', e);
                    await performSearch(buildParams(''));
                } finally {
                    setInitialLoad(false);
                }
            })();
            return;
        }

        if (initialQuery) updateSearchQuery(initialQuery);
        Promise.resolve(performSearch(buildParams(initialQuery))).finally(() => setInitialLoad(false));
    }, []);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        performSearch(buildParams(searchFilters.searchQuery));
    }, [filters.type, filters.status]);

    const filteredInvoices = searchResults || [];

    const PaginationSearch = searchPagination;

    /* Once the located page has rendered and the highlighted invoice is
       actually in the current result set, smoothly scroll it into view. The
       violet pulse + action-button pop is driven purely by the
       `mi-row--highlighted` class in CSS. Runs only once per deep-link. */
    useEffect(() => {
        if (!highlightedInvoiceNumber) return;
        if (initialLoad || isPaging || searchLoading) return;
        if (highlightHandledRef.current) return;
        const exists = (filteredInvoices as any[]).some(
            (inv) => String(inv.invoiceNumber) === String(highlightedInvoiceNumber)
        );
        if (!exists) return;
        highlightHandledRef.current = true;
        const timer = setTimeout(() => {
            const el = document.getElementById('mi-highlight-row');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 260);
        return () => clearTimeout(timer);
    }, [highlightedInvoiceNumber, filteredInvoices, initialLoad, isPaging, searchLoading]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
                setIsSearchDropdownOpen(false);
            }
        };

        if (isDropdownOpen || isSearchDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDropdownOpen, isSearchDropdownOpen]);


    useEffect(() => {
        if (!searchFilters.searchQuery || searchFilters.searchQuery.trim().length === 0) {
            setIsSearchDropdownOpen(false);
        }
    }, [searchFilters.searchQuery]);

    const getPageNumbers = () => {
        const { currentPage, totalPages } = PaginationSearch;
        const pages: (number | string)[] = [];

        if (totalPages <= 5) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (currentPage > 3) {
                pages.push('...');
            }
            const startPage = Math.max(2, currentPage - 1);
            const endPage = Math.min(totalPages - 1, currentPage + 1);
            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
            if (currentPage < totalPages - 2) {
                pages.push('...');
            }
            pages.push(totalPages);
        }

        return pages;
    };

    /* Full-page skeleton ONLY on first mount or pagination — never on
       search/filter typing, because the skeleton replaces the search bar
       and the user loses focus mid-type. Search-triggered loads show a
       body-only skeleton inside the existing table below. */
    if (initialLoad || isPaging) {
        return (
            <div className={`invoice-table-container ${className}`}>
                <InvoiceTableSkeleton columns={8} filters="full" />
            </div>
        );
    }

    /* In-place body skeleton: true while a search/filter fetch is in flight
       but we are not in the initial/paging branches above. */
    const isBodyLoading = searchLoading;

    /* True empty state vs "no search results" empty state */
    const hasActiveQuery = (searchFilters.searchQuery || '').trim().length > 0
        || filters.status !== undefined
        || filters.type !== undefined;
    return (
        <div className={`invoice-table-container ${className}`}>
            <div className="invoice-table-header">
                <h3 className="invoice-table-title">
                    {t('MY_INVOICES')}
                    <span className="mi-count-badge">{searchPagination?.totalCount ?? filteredInvoices.length}</span>
                </h3>

                <div className="invoice-table__filter-bar">
                    <div className="invoice-table__search-input" ref={dropdownRef}>
                        <SearchIcon />
                        <input
                            type="text"
                            placeholder={t('SEARCH_INVOICE_OR_CLIENT', { defaultValue: 'Search invoice or client' })}
                            value={searchFilters.searchQuery}
                            onChange={handleSearchChange}
                            className="invoice-table__search-field"
                        />
                    </div>

                    <select
                        className="invoice-table__filter-select"
                        value={selectedStatus}
                        onChange={(e) => {
                            if (!e.target) return
                            const value = e.target.value;
                            setHighlightedInvoiceNumber(null);
                            setSelectedStatus(value);
                            setStatus(value ? Number(value) : undefined);
                        }}
                    >
                        <option value="">{t('ALL_STATUSES', { defaultValue: 'All statuses' })}</option>
                        <option value="1">{t('PAID') || 'Paid'}</option>
                        <option value="2">{t('UNPAID') || 'Unpaid'}</option>
                        <option value="3">{t('PARTLY_PAID') || 'Partly Paid'}</option>
                    </select>

                    <select
                        className="invoice-table__filter-select"
                        value={selectedStage}
                        onChange={(e) => {
                            if (!e.target) return;
                            const value = e.target.value;
                            setHighlightedInvoiceNumber(null);
                            setSelectedStage(value);
                            setType(value ? Number(value) : undefined);
                        }}
                    >
                        <option value="">{t('ALL_STAGES', { defaultValue: 'All stages' })}</option>
                        <option value="1">{t('SENT') || 'Sent'}</option>
                        <option value="2">{t('COMPLETED') || 'Completed'}</option>
                        <option value="3">{t('PENDING') || 'Pending'}</option>
                    </select>
                </div>
            </div>

            <div className="invoice-table-content">
                {/* Filter Bar */}


                {/* Table */}
                <div className="invoice-table-scroll-wrapper">
                    <div className={`invoice-table mi-active-invoice-table ${String(language).toLowerCase().startsWith('sv') ? 'mi-lang-sv' : ''}`}>
                        {!isMobile && (
                            <TableHeader>
                                <TableColumn>
                                    <span className="mi-th-sort">{t('INVOICE', { defaultValue: 'Invoice' })} <SortArrowIcon /></span>
                                </TableColumn>
                                <TableColumn>{t('CLIENT', { defaultValue: 'Client' })}</TableColumn>
                                <TableColumn>{t('AMOUNT') || 'Amount'}</TableColumn>
                                <TableColumn>{t('DUE_DATE') || 'Due Date'}</TableColumn>
                                <TableColumn>{t('STATUS_LABEL') || 'Status'}</TableColumn>
                                <TableColumn>{t('STAGE_LABEL') || 'Stage'}</TableColumn>
                                <TableColumn>{t('EXPRESS', { defaultValue: 'Express' })}</TableColumn>
                                <TableColumn>{t('ACTIONS', { defaultValue: 'Actions' })}</TableColumn>
                            </TableHeader>
                        )}

                        <TableBody>
                            {/* Body-only skeleton while a search/filter fetch is in flight */}
                            {isBodyLoading && (
                                <>
                                    {Array.from({ length: 6 }).map((_, r) => (
                                        <div className="invoice-table__desktop-row mi-body-skeleton-row" key={`sk-${r}`}>
                                            {Array.from({ length: 8 }).map((_, c) => (
                                                <div className="invoice-table__desktop-cell" key={c}>
                                                    {c === 1 ? (
                                                        <div className="mi-body-skeleton-row__client">
                                                            <span className="mi-body-skeleton-row__avatar" />
                                                            <span className="mi-body-skeleton-row__line" />
                                                        </div>
                                                    ) : (
                                                        <span className="mi-body-skeleton-row__line" />
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </>
                            )}

                            {/* Empty state — true empty vs "no search results" */}
                            {!isBodyLoading && filteredInvoices.length === 0 && (
                                <div className="mi-empty-state">
                                    <div className="mi-empty-state__icon" aria-hidden="true">
                                        {hasActiveQuery ? (
                                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                                                <path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                                <path d="M9 11h4M11 9v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
                                            </svg>
                                        ) : (
                                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                                <path d="M9 2h6a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6l4-4z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                                                <path d="M9 2v4h4" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                                                <path d="M9 14h6M9 18h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                            </svg>
                                        )}
                                    </div>
                                    <h4 className="mi-empty-state__title">
                                        {hasActiveQuery
                                            ? t('NO_RESULTS_FOUND', { defaultValue: 'No matching invoices' })
                                            : t('NO_INVOICES_YET')}
                                    </h4>
                                    <p className="mi-empty-state__message">
                                        {hasActiveQuery
                                            ? t('NO_RESULTS_HINT', { defaultValue: 'Try a different search term or clear your filters.' })
                                            : t('NO_INVOICES_HINT', { defaultValue: 'Your invoices will show up here once you create them.' })}
                                    </p>
                                </div>
                            )}

                            {!isBodyLoading && filteredInvoices.length > 0 && (isMobile ? (
                                // Mobile: Card-based layout
                                (Array.isArray(filteredInvoices) ? filteredInvoices : [])?.map((invoice: any) => {
                                    const isHighlighted = highlightedInvoiceNumber != null
                                        && String(invoice.invoiceNumber) === String(highlightedInvoiceNumber);
                                    return (
                                    <MobileCardWrapper
                                        key={invoice.id}
                                        id={isHighlighted ? 'mi-highlight-row' : undefined}
                                        className={isHighlighted ? 'mi-row--highlighted' : ''}
                                    >
                                        <MobileCardRow label={t('INVOICE_NO') || 'Invoice No.'}>
                                            {/* Native tooltip surfaces the full invoice number
                                                on hover when the column is too narrow to fit
                                                it — see TruncatedText helper at top of file. */}
                                            <MiddleTruncatedText className="mi-invoice__number" text={String(invoice.invoiceNumber ?? '')} />
                                        </MobileCardRow>
                                        <MobileCardRow label={t('CLIENT_NAME') || 'Client Name'}>
                                            <span className="mi-client">
                                                <span className={`mi-client__avatar mi-client__avatar--v${getAvatarVariant(invoice?.customer?.name)}`}>{getClientInitial(invoice?.customer?.name)}</span>
                                                <span className="mi-client__info">
                                                    {/* Tooltip only appears when the name is clipped —
                                                        short names still render without one. */}
                                                    <TruncatedText className="mi-client__name" text={String(invoice?.customer?.name ?? '')} />
                                                </span>
                                            </span>
                                        </MobileCardRow>
                                        <MobileCardRow label={t('AMOUNT') || 'Amount'}>
                                            <span className="mi-amount">{formatAmount(invoice.amount)} {invoice.currency}</span>
                                        </MobileCardRow>
                                        <MobileCardRow label={t('DUE_DATE') || 'Due Date'}>
                                            <span className="mi-due">
                                                <span className="mi-due__date" title={formatDisplayDate(invoice.dueDate)}><CalendarIcon /> {formatDisplayDate(invoice.dueDate)}</span>
                                                <span className="mi-due__rel">{getRelativeDue(invoice.dueDate)}</span>
                                            </span>
                                        </MobileCardRow>
                                        <MobileCardRow label={t('STATUS_LABEL') || 'Status'}>
                                            <span className={`${getStatusBadgeClass(invoice.invoiceStatus)} mi-status-badge`}>
                                                <span className="mi-dot" />
                                                {t(mapInvoiceStatus(invoice.invoiceStatus)) || mapInvoiceStatus(invoice.invoiceStatus)}
                                            </span>
                                        </MobileCardRow>
                                        <MobileCardRow label={t('STAGE_LABEL') || 'Stage'}>
                                            {(() => {
                                                const stageStatus = sentInvoicesStatus(invoice);
                                                return (
                                                    <span className={getStageBadgeClass(stageStatus)}>
                                                        {t(stageStatus)}
                                                    </span>
                                                );
                                            })()}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('EXPRESS_PAYMENT_LABEL') || 'Express Payment'}>
                                            {invoice.expressPayout ? (
                                                <span className="mi-express mi-express--on" title={t('APPROVED')}>
                                                    <LightningIcon /> {t('EXPRESS', { defaultValue: 'Express' })}
                                                </span>
                                            ) : (
                                                <span className="mi-express mi-express--none" title={t('NOTAPPLIED')}>{t('NOTAPPLIED')}</span>
                                            )}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('ACTION_LABEL') || 'Action'}>
                                            <div className="invoice-table-detailed__action-group">
                                                <div className={`invoice-tracking-btn-wrapper${!invoice.isEmailSent || !invoice.emailStatus ? ' invoice-tracking-btn-wrapper--disabled' : ''}`}>
                                                    <button
                                                        className="invoice-table-detailed__action-btn"
                                                        data-tip={
                                                            !invoice.isEmailSent
                                                                ? t('INVOICE_NOT_APPROVED_BY_ADMIN')
                                                                : !invoice.emailStatus
                                                                    ? t('INVOICE_TRACKING_NOT_AVAILABLE')
                                                                    : t('INVOICE_TRACKING')
                                                        }
                                                        onClick={() => handleOpenEmailTracking(invoice)}
                                                        disabled={!invoice.isEmailSent || !invoice.emailStatus}
                                                    >
                                                        <TrackingIcon />
                                                        <span className="invoice-table-detailed__action-label">{t('MI_ACTION_TRACK', { defaultValue: 'Track' })}</span>
                                                    </button>
                                                </div>
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    data-tip={
                                                        invoice.isEditInvoiceRequested && invoice.isEditInvoiceRequestApproved
                                                            ? t("INVOICE_EDIT_APPROVED")
                                                            : invoice.isEditInvoiceRequested && invoice.isEditInvoiceRequestRejected
                                                                ? t("INVOICE_EDIT_REJECTED")
                                                                : invoice.isEditInvoiceRequested
                                                                    ? t("INVOICE_EDIT_PENDING")
                                                                    : t("EDIT_INVOICE")
                                                    } onClick={() => handleEditInvoiceButtonClick(invoice)}
                                                    disabled={invoice.isEditInvoiceRequested || invoice.isHaveCreditInvoice || invoice.isCreditInvoiceRequested}
                                                >
                                                    <EditIcon
                                                        color={invoice.isEditInvoiceRequested && invoice.isEditInvoiceRequestApproved
                                                            ? "green"
                                                            : invoice.isEditInvoiceRequested && invoice.isEditInvoiceRequestRejected
                                                                ? "red"
                                                                : undefined}
                                                        opacity={
                                                            !!invoice.isEditInvoiceRequested && !invoice.isEditInvoiceRequestApproved && !invoice.isEditInvoiceRequestRejected && invoice.isBooked
                                                                ? 0.5
                                                                : invoice?.isHaveCreditInvoice ? 0.5 : invoice?.isCreditInvoiceRequested ? 0.5 : 1
                                                        }
                                                    />
                                                    <span className="invoice-table-detailed__action-label">{t('MI_ACTION_EDIT', { defaultValue: 'Edit' })}</span>
                                                </button>
                                                <div className={`invoice-download-btn-wrapper${!invoice.isEmailSent ? ' invoice-download-btn-wrapper--disabled' : ''}`}>
                                                    <button
                                                        className="invoice-table-detailed__action-btn"
                                                        data-tip={invoice.isEmailSent ? t('DOWNLOAD', { defaultValue: 'Download' }) : t('INVOICE_NOT_APPROVED_BY_ADMIN')}
                                                        onClick={() => {
                                                            const invoceLang = invoice.customer?.country === "Sweden" ? 'sv-se' : 'en-us';
                                                            handleDownloadInvoice(invoice.invoiceNumber, invoceLang);
                                                        }}
                                                        disabled={downloadingInvoiceId === invoice.id || !invoice.isEmailSent}
                                                    >
                                                        <DownloadIcon />
                                                        <span className="invoice-table-detailed__action-label">{t('MI_ACTION_DOWNLOAD', { defaultValue: 'Download' })}</span>
                                                    </button>
                                                </div>
                                                <button
                                                    className="invoice-table-detailed__action-btn invoice-send-btn"
                                                    data-tip={t(getSendInvoiceBlockedKey(invoice) ?? "SEND_INVOICE_TO_CUSTOMER")}
                                                    disabled={getSendInvoiceBlockedKey(invoice) !== null}
                                                    onClick={() => handleSendInvoice(invoice)}
                                                >
                                                    <SendIcon opacity={getSendInvoiceBlockedKey(invoice) !== null ? 0.5 : 1} />
                                                    <span className="invoice-table-detailed__action-label">{t('MI_ACTION_SEND', { defaultValue: 'Send' })}</span>
                                                </button>
                                                <button
                                                    className="invoice-table-detailed__action-btn invoice-credit-btn"
                                                    data-tip={invoice?.isHaveCreditInvoice ? t("CREDIT_NOTE_ALREADY_REQUESTED") : t("REQUEST_CREDIT_INVOICE")}
                                                    disabled={invoice?.isHaveCreditInvoice || (invoice.isEditInvoiceRequested && !invoice?.isEditInvoiceRequestApproved && !invoice?.isEditInvoiceRequestRejected) || invoice?.isSalaryCreated}
                                                    onClick={() => handleRequestCreditInvoice(invoice)}
                                                >
                                                    <RequestCreditIcon opacity={invoice?.isHaveCreditInvoice ? 0.5 : (invoice.isEditInvoiceRequested && !invoice?.isEditInvoiceRequestApproved && !invoice?.isEditInvoiceRequestRejected) ? 0.5 : invoice?.isSalaryCreated ? 0.5 : 1} />
                                                    <span className="invoice-table-detailed__action-label">{t('MI_ACTION_CREDIT', { defaultValue: 'Credit' })}</span>
                                                </button>
                                            </div>
                                        </MobileCardRow>
                                    </MobileCardWrapper>
                                    )
})
                            ) : (
                                (filteredInvoices as any[]).map((invoice: any) => {
                                    const isHighlighted = highlightedInvoiceNumber != null
                                        && String(invoice.invoiceNumber) === String(highlightedInvoiceNumber);
                                    return(
                                    <TableRow
                                        key={invoice.id}
                                        id={isHighlighted ? 'mi-highlight-row' : undefined}
                                        className={isHighlighted ? 'mi-row--highlighted' : ''}
                                    >
                                        <TableCell>
                                            {/* Native tooltip surfaces the full invoice number
                                                on hover when the column is too narrow to fit
                                                it — see TruncatedText helper at top of file. */}
                                            <MiddleTruncatedText className="mi-invoice__number" text={String(invoice.invoiceNumber ?? '')} />
                                        </TableCell>
                                        <TableCell>
                                            <span className="mi-client">
                                                <span className={`mi-client__avatar mi-client__avatar--v${getAvatarVariant(invoice?.customer?.name ?? invoice.clientName)}`}>{getClientInitial(invoice?.customer?.name ?? invoice.clientName)}</span>
                                                <span className="mi-client__info">
                                                    {/* Tooltip only appears when the name is clipped —
                                                        short names still render without one. */}
                                                    <TruncatedText className="mi-client__name" text={String(invoice?.customer?.name ?? invoice.clientName ?? '')} />
                                                </span>
                                            </span>
                                        </TableCell>
                                        <TableCell><TruncatedText className="mi-amount" text={`${formatAmount(invoice.amount)} ${invoice.currency}`} /></TableCell>
                                        <TableCell>
                                            <span className="mi-due">
                                                <span className="mi-due__date" title={formatDisplayDate(invoice.dueDate)}><CalendarIcon /> {formatDisplayDate(invoice.dueDate)}</span>
                                                <span className="mi-due__rel">{getRelativeDue(invoice.dueDate)}</span>
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <span className={`${getStatusBadgeClass(invoice?.invoiceStatus)} mi-status-badge`}>
                                                <span className="mi-dot" />
                                                {/* {invoice.invoiceStatus === 1 &&

                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width={12}
                                                        height={12}
                                                        viewBox="0 0 12 12"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M9 3L3 9M9 9.00001L3 3.00001"
                                                            stroke="#252525"
                                                            strokeWidth="1.33"
                                                            strokeLinecap="round"
                                                        />
                                                    </svg>
                                                } */}

                                                {/* {invoice.invoiceStatus === 2 && <CheckIcon />} */}
                                                {t(mapInvoiceStatus(invoice.invoiceStatus)) || mapInvoiceStatus(invoice.invoiceStatus)}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            {(() => {
                                                const stageStatus = sentInvoicesStatus(invoice);
                                                return (
                                                    <span className={getStageBadgeClass(stageStatus)}>
                                                        {t(stageStatus)}
                                                    </span>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell>
                                            {invoice.expressPayout ? (
                                                <span className="mi-express mi-express--on" title={t('APPROVED')}>
                                                    <LightningIcon /> {t('EXPRESS', { defaultValue: 'Express' })}
                                                </span>
                                            ) : (
                                                <span className="mi-express mi-express--none" title={t('NOTAPPLIED')}>{t('NOTAPPLIED')}</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div className="invoice-table-detailed__action-group">
                                                <div className={`invoice-tracking-btn-wrapper${!invoice.isEmailSent || !invoice.emailStatus ? ' invoice-tracking-btn-wrapper--disabled' : ''}`}>
                                                    <button
                                                        className="invoice-table-detailed__action-btn"
                                                        data-tip={
                                                            !invoice.isEmailSent
                                                                ? t('INVOICE_NOT_APPROVED_BY_ADMIN')
                                                                : !invoice.emailStatus
                                                                    ? t('INVOICE_TRACKING_NOT_AVAILABLE')
                                                                    : t('INVOICE_TRACKING')
                                                        }
                                                        onClick={() => handleOpenEmailTracking(invoice)}
                                                        disabled={!invoice.isEmailSent || !invoice.emailStatus}
                                                    >
                                                        <TrackingIcon />
                                                        <span className="invoice-table-detailed__action-label">{t('MI_ACTION_TRACK', { defaultValue: 'Track' })}</span>
                                                    </button>
                                                </div>
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    data-tip={
                                                        invoice.isEditInvoiceRequested && invoice.isEditInvoiceRequestApproved
                                                            ? t("INVOICE_EDIT_APPROVED")
                                                            : invoice.isEditInvoiceRequested && invoice.isEditInvoiceRequestRejected
                                                                ? t("INVOICE_EDIT_REJECTED")
                                                                : invoice.isEditInvoiceRequested
                                                                    ? t("INVOICE_EDIT_PENDING")
                                                                    : t("EDIT_INVOICE")
                                                    }
                                                    onClick={() => handleEditInvoiceButtonClick(invoice)}
                                                    disabled={invoice.isEditInvoiceRequested || invoice.isHaveCreditInvoice || invoice.isCreditInvoiceRequested}
                                                >
                                                    <EditIcon
                                                        color={invoice.isEditInvoiceRequested && invoice.isEditInvoiceRequestApproved
                                                            ? "green"
                                                            : invoice.isEditInvoiceRequested && invoice.isEditInvoiceRequestRejected
                                                                ? "red"
                                                                : undefined}
                                                        opacity={
                                                            !!invoice.isEditInvoiceRequested && !invoice.isEditInvoiceRequestApproved && !invoice.isEditInvoiceRequestRejected && invoice.isBooked
                                                                ? 0.5
                                                                : invoice?.isHaveCreditInvoice ? 0.5 : invoice?.isCreditInvoiceRequested ? 0.5 : 1
                                                        }
                                                    />
                                                    <span className="invoice-table-detailed__action-label">{t('MI_ACTION_EDIT', { defaultValue: 'Edit' })}</span>
                                                </button>
                                                <div className={`invoice-download-btn-wrapper${!invoice.isEmailSent ? ' invoice-download-btn-wrapper--disabled' : ''}`}>
                                                    <button
                                                        className="invoice-table-detailed__action-btn"
                                                        data-tip={invoice.isEmailSent ? t('DOWNLOAD', { defaultValue: 'Download' }) : t('INVOICE_NOT_APPROVED_BY_ADMIN')}
                                                        onClick={() => {
                                                            const invoceLang = invoice.customer?.country == "Sweden" ? 'sv-se' : 'en-us';
                                                            handleDownloadInvoice(invoice.invoiceNumber, invoceLang);
                                                        }}
                                                        disabled={downloadingInvoiceId === invoice.id || !invoice.isEmailSent}
                                                    >
                                                        <DownloadIcon />
                                                        <span className="invoice-table-detailed__action-label">{t('MI_ACTION_DOWNLOAD', { defaultValue: 'Download' })}</span>
                                                    </button>
                                                </div>

                                                <button
                                                    className="invoice-table-detailed__action-btn invoice-send-btn"
                                                    data-tip={t(getSendInvoiceBlockedKey(invoice) ?? "SEND_INVOICE_TO_CUSTOMER")}
                                                    disabled={getSendInvoiceBlockedKey(invoice) !== null}
                                                    onClick={() => handleSendInvoice(invoice)}
                                                >
                                                    <SendIcon opacity={getSendInvoiceBlockedKey(invoice) !== null ? 0.5 : 1} />
                                                    <span className="invoice-table-detailed__action-label">{t('MI_ACTION_SEND', { defaultValue: 'Send' })}</span>
                                                </button>
                                                <button
                                                    className="invoice-table-detailed__action-btn invoice-credit-btn"
                                                    data-tip={invoice?.isHaveCreditInvoice ? t("CREDIT_NOTE_ALREADY_REQUESTED") : t("REQUEST_CREDIT_INVOICE")}
                                                    disabled={invoice?.isHaveCreditInvoice || (invoice.isEditInvoiceRequested && !invoice?.isEditInvoiceRequestApproved && !invoice?.isEditInvoiceRequestRejected) || invoice?.isSalaryCreated}
                                                    onClick={() => handleRequestCreditInvoice(invoice)}
                                                >
                                                    <RequestCreditIcon opacity={invoice?.isHaveCreditInvoice ? 0.5 : (invoice.isEditInvoiceRequested && !invoice?.isEditInvoiceRequestApproved && !invoice?.isEditInvoiceRequestRejected) ? 0.5 : invoice?.isSalaryCreated ? 0.5 : 1} />
                                                    <span className="invoice-table-detailed__action-label">{t('MI_ACTION_CREDIT', { defaultValue: 'Credit' })}</span>
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                    )}
                                )
                            ))}
                        </TableBody>
                    </div>
                </div>

                {/* Pagination */}
                {PaginationSearch.totalPages > 0 && (
                    <div className="invoice-table-footer">
                        <span className="invoice-table-footer__count">
                            {(() => {
                                const total = (PaginationSearch as any).totalCount ?? 0;
                                const size = (PaginationSearch as any).pageSize ?? 10;
                                const start = total === 0 ? 0 : (PaginationSearch.currentPage - 1) * size + 1;
                                const end = Math.min(PaginationSearch.currentPage * size, total);
                                return t('SHOWING_INVOICES_COUNT', {
                                    start,
                                    end,
                                    total,
                                    defaultValue: 'Showing {{start}}–{{end}} of {{total}} invoices',
                                });
                            })()}
                        </span>
                        <div className="invoice-table-pagination">
                        <div className="pagination-controls">
                            <button
                                className="pagination-button pagination-button--prev"
                                onClick={() => goToPage(PaginationSearch.currentPage - 1)}
                                disabled={PaginationSearch.currentPage === 1}
                            >
                                <ArrowLeftIcon />
                                <span>{t('PREV_STEP') || 'Prev'}</span>
                            </button>

                            {getPageNumbers().map((page, index) => {
                                if (page === '...') {
                                    return (
                                        <button
                                            key={`ellipsis-${index}`}
                                            className="pagination-button pagination-button--number"
                                            disabled
                                        >
                                            ...
                                        </button>
                                    );
                                }
                                return (
                                    <button
                                        key={page}
                                        className={`pagination-button pagination-button--number${page === PaginationSearch.currentPage ? ' pagination-button--active' : ''
                                            }`}
                                        onClick={() => goToPage(page as number)}
                                    >
                                        {page}
                                    </button>
                                );
                            })}

                            <button
                                className="pagination-button pagination-button--next"
                                onClick={() => goToPage(PaginationSearch.currentPage + 1)}
                                disabled={PaginationSearch.currentPage === PaginationSearch.totalPages}
                            >
                                <span>{t('NEXT_STEP') || 'Next'}</span>  
                                <ArrowRightIcon />
                            </button>
                        </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Version Restriction Modal */}
            <Modal
                show={showVersionRestrictionModal}
                onHide={() => setShowVersionRestrictionModal(false)}
                centered
                className="version-restriction-modal"
                // size="sm"
                // style={{ '--bs-modal-width': '200px' } as React.CSSProperties}
                // style={{width:"20% !important",}}
            >
                <Modal.Header closeButton={false} style={{ border: 'none', paddingBottom: '0' }}>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowVersionRestrictionModal(false);
                        }}
                        aria-label="Close"
                        style={{
                            position: 'absolute',
                            right: '20px',
                            top: '20px',
                            background: '#7C3AED',
                            border: '1px solid #7C3AED',
                            cursor: 'pointer',
                            padding: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '25px',
                            transition: 'none',
                            boxShadow: 'none',
                            zIndex: 1000
                        }}
                    >
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M15 5L5 15M5 5L15 15"
                                stroke="#FFFFFF"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                </Modal.Header>
                <Modal.Body style={{ padding: '2rem 2.5rem', textAlign: 'center' }}>
                    <h4 style={{
                        fontSize: '1.25rem',
                        fontWeight: '600',
                        color: '#252525',
                        marginBottom: '0.75rem',
                        lineHeight: '1.4'
                    }}>
                        {t('VERSION_RESTRICTION_TITLE') || 'Version Restriction'}
                    </h4>
                    <p style={{
                        fontSize: '0.95rem',
                        color: '#666',
                        lineHeight: '1.6',
                        marginBottom: '0',
                        maxWidth: '400px',
                        margin: '0 auto'
                    }}>
                        {t('VERSION_RESTRICTION_MESSAGE') || 'This invoice was created with Version 1 of the invoice tool. To edit this invoice, please switch to Version 1.'}
                    </p>
                </Modal.Body>
                <Modal.Footer style={{
                    border: 'none',
                    padding: '0 2.5rem 2rem',
                    justifyContent: 'center'
                }}>
                    <button
                        type="button"
                        onClick={() => setShowVersionRestrictionModal(false)}
                        style={{
                            backgroundColor: '#7C3AED',
                            color: 'white',
                            border: 'none',
                            borderRadius: '25px',
                            padding: '0.65rem 2rem',
                            fontSize: '0.95rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'none',
                            minWidth: '120px'
                        }}
                    >
                        {t('CLOSE') || 'Close'}
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Request Credit Invoice Modal */}
            <RequestCreditInvoiceModal
                show={showCreditInvoiceModal}
                invoice={selectedInvoiceForCredit}
                onClose={handleCloseCreditInvoiceModal}
                onSuccess={handleCreditInvoiceSuccess}
            />

            {/* Send / resend an approved invoice to the customer */}
            <SendInvoiceToCustomerModal
                show={showSendInvoiceModal}
                invoice={selectedInvoiceForSend}
                onClose={handleCloseSendInvoiceModal}
                onSuccess={handleSendInvoiceSuccess}
            />

            {/* Email Tracking Details Modal */}
            <EmailTrackingModal
                show={trackingInvoice !== null}
                invoiceId={trackingInvoice?.invoiceId ?? null}
                invoiceStatus={trackingInvoice?.invoiceStatus}
                isSalaryCreated={trackingInvoice?.isSalaryCreated}
                invoiceSalaryStatus={trackingInvoice?.invoiceSalaryStatus}
                onClose={handleCloseEmailTracking}
            />
        </div >
    );
};
